import React from 'react';
import { C } from '../constants';
import { obtenerEstudioFlux, esHandleFlux } from '../services/estudioFlux';

// ─────────────────────────────────────────────────────────────────────────────
// EL ESTUDIO DE FLUXAI, VISTO DESDE SOLAR HUB.
//
// Decisión del dueño (2026-08-09): el estudio fotovoltaico de alto nivel —el que se arma sobre la
// telemetría REAL del medidor— SE QUEDA EN FLUXAI, y Solar Hub lo LEE. El lector
// (`services/estudioFlux.js`) llevaba tiempo escrito y probado, pero NINGUNA pantalla lo montaba:
// el dato llegaba hasta el borde y ahí se quedaba.
//
// 🔑 SE LEE, NO SE COPIA. El estudio tiene UN dueño. Guardar acá una copia traería el modo de falla
// que este diseño evita: dos versiones y nadie sabe cuál está al día. Por eso no hay estado
// persistido — se pide por el handle y se muestra en vivo (la caché del servicio es de 5 min).
//
// 🔴 POR QUÉ EL DOCUMENTO LLEGA COMO HTML Y NO COMO DATOS: la memoria técnica RETIE la RENDERIZA
// FluxAI. Armarla acá reviviría el generador de Solar Hub, que es justo el que este plan está
// paralizando — y dos generadores del mismo documento firmable terminan discrepando. El que se
// radica ante el operador de red no puede ser el que quedó viejo.
// ─────────────────────────────────────────────────────────────────────────────

const box = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 14,
};

function Dato({ k, v, u }) {
  if (v == null || v === '') return null;
  return (
    <div style={{ minWidth: 150 }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{k}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
        {typeof v === 'number' ? v.toLocaleString('es-CO', { maximumFractionDigits: 2 }) : v}
        {u ? <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}> {u}</span> : null}
      </div>
    </div>
  );
}

export default function EstudioFlux({ handleInicial = '' }) {
  const [handle, setHandle] = React.useState(handleInicial);
  const [estudio, setEstudio] = React.useState(null);
  const [cargando, setCargando] = React.useState(false);
  // `buscado` distingue «todavía no preguntaste» de «pregunté y no hay». Sin él, la pantalla vacía
  // dice lo mismo en los dos casos y el usuario no sabe si el handle está mal o si no existe.
  const [buscado, setBuscado] = React.useState(false);
  const [verMemoria, setVerMemoria] = React.useState(false);

  const formaOk = esHandleFlux(handle);

  const buscar = React.useCallback(async () => {
    if (!formaOk) return;
    setCargando(true);
    try {
      const e = await obtenerEstudioFlux(handle);
      setEstudio(e);
    } finally {
      setCargando(false);
      setBuscado(true);
    }
  }, [handle, formaOk]);

  // Si llega un handle desde afuera (una cotización que nació de un estudio), se busca solo: el
  // usuario no tiene por qué copiar y pegar algo que la app ya sabe.
  React.useEffect(() => {
    if (handleInicial && esHandleFlux(handleInicial)) {
      setHandle(handleInicial);
      obtenerEstudioFlux(handleInicial).then((e) => { setEstudio(e); setBuscado(true); });
    }
  }, [handleInicial]);

  const doc = estudio && estudio.documentos && estudio.documentos.memoria_tecnica_html;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '18px 16px 40px' }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: '0 0 6px' }}>Estudio fotovoltaico</h2>
      <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, margin: '0 0 16px' }}>
        Ingeniería de detalle armada sobre la <strong>telemetría real</strong> del medidor: simulación
        horaria 8760 h, pérdidas, unifilar y memoria técnica RETIE. Se muestra <strong>en vivo</strong>
        {' '}desde FluxAI — acá no se guarda copia, así que lo que ves es siempre la última versión.
      </p>

      <div style={box}>
        <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 6 }}>
          Identificador del estudio
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={handle}
            onChange={(e) => { setHandle(e.target.value.trim()); setBuscado(false); setEstudio(null); }}
            placeholder="fx:00000000-0000-0000-0000-000000000000"
            style={{
              flex: 1, minWidth: 280, padding: '9px 11px', borderRadius: 8,
              border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 13,
              fontFamily: 'ui-monospace, monospace',
            }}
          />
          <button
            type="button" onClick={buscar} disabled={!formaOk || cargando}
            style={{
              background: formaOk ? C.yellow : C.border, color: formaOk ? '#1a1205' : C.muted,
              border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 800, fontSize: 13,
              cursor: formaOk && !cargando ? 'pointer' : 'default',
            }}
          >{cargando ? 'Buscando…' : 'Ver estudio'}</button>
        </div>
        {handle && !formaOk && (
          <p style={{ fontSize: 12, color: C.muted, margin: '8px 0 0' }}>
            Ese no es un identificador de estudio. Tiene la forma <code>fx:</code> + UUID y lo acuña
            FluxAI al guardar el estudio.
          </p>
        )}
      </div>

      {/* «No hay» y «no se pudo» se ven igual desde acá a propósito: el lector devuelve null en los
          dos casos, y para el usuario la acción es la misma. Decirle «error de conexión» cuando el
          estudio simplemente no existe lo mandaría a revisar su internet. */}
      {buscado && !estudio && !cargando && (
        <div style={{ ...box, borderColor: C.yellow }}>
          <strong style={{ color: C.text }}>No hay estudio para ese identificador.</strong>
          <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0', lineHeight: 1.5 }}>
            Puede que aún no se haya guardado en FluxAI, o que el identificador no sea el de este
            proyecto. Se guarda desde el Estudio fotovoltaico de FluxAI, con «Guardar estudio».
          </p>
        </div>
      )}

      {estudio && (
        <>
          <div style={box}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
              <Dato k="Potencia" v={estudio.sistema && estudio.sistema.kwp} u="kWp" />
              <Dato k="Módulos" v={estudio.sistema && estudio.sistema.modulos} />
              <Dato k="Inversor" v={estudio.sistema && estudio.sistema.inversor_kw} u="kW" />
              <Dato k="Producción" v={estudio.sistema && estudio.sistema.produccion_kwh_anio} u="kWh/año" />
              <Dato k="Cobertura" v={estudio.sistema && estudio.sistema.cobertura_pct} u="%" />
              <Dato k="PR" v={estudio.simulacion && estudio.simulacion.pr} />
            </div>
            {/* La PROCEDENCIA del perfil es el diferencial de este estudio: un perfil medido con 12
                días y otro con 365 no valen lo mismo, y sin decirlo se presentan igual. */}
            {estudio.consumo && estudio.consumo.periodo && (
              <p style={{ fontSize: 12, color: C.muted, margin: '12px 0 0', lineHeight: 1.5 }}>
                Perfil de consumo <strong>medido</strong> con {estudio.consumo.periodo.dias_datos ?? '—'} días
                de telemetría ({estudio.consumo.periodo.cobertura_pct ?? '—'}% del período)
                {estudio.emitido ? ` · emitido ${String(estudio.emitido).slice(0, 10)}` : ''}
                {estudio.actualizado ? ` · actualizado ${String(estudio.actualizado).slice(0, 10)}` : ''}.
              </p>
            )}
          </div>

          <div style={box}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <strong style={{ color: C.text, fontSize: 15 }}>Memoria Técnica Eléctrica</strong>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
                  RETIE · NTC 2050 · CREG 174/2021 — documento firmable para radicar ante el operador de red.
                </div>
              </div>
              {doc && (
                <button
                  type="button" onClick={() => setVerMemoria((v) => !v)}
                  style={{
                    background: C.yellow, color: '#1a1205', border: 'none', borderRadius: 8,
                    padding: '9px 16px', fontWeight: 800, fontSize: 13, cursor: 'pointer',
                  }}
                >{verMemoria ? 'Ocultar' : 'Ver memoria'}</button>
              )}
            </div>

            {/* Un estudio guardado ANTES de que la memoria viajara no la trae. Se dice por qué, en
                vez de mostrar un botón muerto o un espacio en blanco. */}
            {!doc && (
              <p style={{ fontSize: 13, color: C.muted, margin: '10px 0 0', lineHeight: 1.5 }}>
                Este estudio no trae la memoria. Se genera al <strong>volver a guardarlo</strong> en
                FluxAI; los guardados antes de agosto de 2026 no la incluyen.
              </p>
            )}

            {doc && verMemoria && (
              <iframe
                title="Memoria técnica"
                srcDoc={doc}
                sandbox="allow-same-origin allow-modals"
                style={{
                  width: '100%', height: '75vh', marginTop: 12, borderRadius: 8,
                  border: `1px solid ${C.border}`, background: '#fff',
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
